import React, { useState } from 'react';
import Header from '../components/Header';
import Newsletter from '../components/Newsletter';
import Footer from '../components/Footer';

interface ReservationData {
  date: string;
  timeSlot: string;
  bikeType: string;
  forfait: string;
  forfaitDescription: string;
  prix: number;
  name: string;
  email: string;
  phone: string;
}

interface Forfait {
  id: string;
  nom: string;
  sousTitre: string;
  prix: number;
  image: string;
}

interface BikeType {
  id: string;
  name: string;
  icon: string;
  description: string;
}

const ReservationLavage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date(2026, 0, 1));
  const [reservationData, setReservationData] = useState<ReservationData>({
    date: '',
    timeSlot: '',
    bikeType: '',
    forfait: '',
    forfaitDescription: '',
    prix: 0,
    name: '',
    email: '',
    phone: ''
  });

  const steps = [
    { number: 1, title: "Date & Heure", icon: "📅" },
    { number: 2, title: "Type de vélo", icon: "🚴" },
    { number: 3, title: "Forfait", icon: "✨" },
    { number: 4, title: "Coordonnées", icon: "👤" },
    { number: 5, title: "Confirmation", icon: "✓" }
  ];

  const bikeTypes: BikeType[] = [
    { 
      id: 'route', 
      name: 'Vélo Route', 
      icon: '/type velo loc/premium.png', 
      description: 'Vélo de route classique ou aéro' 
    },
    { 
      id: 'gravel', 
      name: 'Vélo Gravel', 
      icon: '/type velo loc/gravel.png', 
      description: 'Vélo tout-terrain polyvalent' 
    },
    { 
      id: 'triathlon', 
      name: 'Vélo Triathlon', 
      icon: '/type velo loc/triathlon.png', 
      description: 'Vélo contre-la-montre ou triathlon' 
    }
  ];

  const timeSlots = [
    '09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00', '18:00'
  ];

  const forfaits: Forfait[] = [
    {
      id: 'or',
      nom: 'Forfait Or',
      sousTitre: 'Nettoyage complet & finition',
      prix: 45,
      image: '/lavage/fe5b4dbd567cfb5a627c191ee642fcb5.jpg'
    },
    {
      id: 'argent',
      nom: 'Forfait Argent',
      sousTitre: 'Nettoyage approfondi',
      prix: 30,
      image: '/lavage/6baa0e222c53925f1d4f02a97f857cbc.jpg'
    },
    {
      id: 'bronze',
      nom: 'Forfait Bronze',
      sousTitre: 'Lavage express',
      prix: 15,
      image: '/lavage/8ac0498e22021a140ef30d7f9d5de57b.jpg'
    }
  ];

  // Calendar helpers
  const getDaysInMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date): number => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const formatMonth = (date: Date): string => {
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const formatSelectedDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const isToday = (day: number): boolean => {
    const today = new Date();
    return today.getDate() === day &&
      today.getMonth() === currentMonth.getMonth() &&
      today.getFullYear() === currentMonth.getFullYear();
  };

  const isPastDay = (day: number): boolean => {
    const today = new Date();
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    today.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  const handleDateSelect = (day: number) => {
    const selected = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateStr = selected.toISOString().split('T')[0];
    setSelectedDate(dateStr);
    setReservationData({ ...reservationData, date: dateStr });
  };

  const handleTimeSlotSelect = (time: string) => {
    setReservationData({ ...reservationData, timeSlot: time });
  };

  const handleBikeTypeSelect = (type: string) => {
    setReservationData({ ...reservationData, bikeType: type });
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleForfaitSelect = (forfait: Forfait) => {
    setReservationData({ 
      ...reservationData, 
      forfait: forfait.nom,
      forfaitDescription: forfait.sousTitre,
      prix: forfait.prix 
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setReservationData({ ...reservationData, [name]: value });
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePayment = () => {
    alert('Redirection vers le paiement...');
  };

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 1:
        return !!selectedDate && !!reservationData.timeSlot;
      case 2:
        return !!reservationData.bikeType;
      case 3:
        return !!reservationData.forfait;
      case 4:
        return !!reservationData.name && !!reservationData.email && !!reservationData.phone;
      default:
        return true;
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];
    const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toISOString().split('T')[0];
      const isSelected = selectedDate === dateStr;
      const isSunday = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).getDay() === 0;
      const isPast = isPastDay(day);
      const isDisabled = isSunday || isPast;
      const isTodayDate = isToday(day);

      days.push(
        <button
          key={day}
          onClick={() => !isDisabled && handleDateSelect(day)}
          disabled={isDisabled}
          className={`h-10 w-10 rounded-lg flex items-center justify-center text-sm transition-all font-medium
            ${isSelected ? 'bg-black text-white scale-110 shadow-lg' : ''}
            ${isTodayDate && !isSelected ? 'ring-2 ring-black text-black font-bold' : ''}
            ${isDisabled ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-700 cursor-pointer hover:scale-105'}
          `}
        >
          {day}
        </button>
      );
    }

    return (
      <div className="bg-white rounded-xl p-6 max-w-md shadow-lg border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <button onClick={handlePrevMonth} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-lg font-bold capitalize">{formatMonth(currentMonth)}</h3>
          <button onClick={handleNextMonth} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map(day => (
            <div key={day} className="h-10 flex items-center justify-center text-xs font-semibold text-gray-500">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days}
        </div>

        {selectedDate && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg text-center">
            <p className="text-xs text-gray-500 mb-1">Date sélectionnée</p>
            <p className="text-sm font-semibold capitalize">{formatSelectedDate(selectedDate)}</p>
          </div>
        )}

        {selectedDate && (
          <>
            <div className="mt-6 mb-2">
              <h4 className="text-sm font-semibold mb-3">Choisissez un créneau horaire</h4>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {timeSlots.map((time) => (
                <button
                  key={time}
                  onClick={() => handleTimeSlotSelect(time)}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    reservationData.timeSlot === time
                      ? 'bg-black text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="flex justify-center">
            {renderCalendar()}
          </div>
        );

      case 2:
        return (
          <div className="flex justify-center">
            <div className="max-w-3xl">
              <h3 className="text-2xl font-bold text-center mb-6">Quel type de vélo ?</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {bikeTypes.map((type) => (
                  <div
                    key={type.id}
                    onClick={() => handleBikeTypeSelect(type.id)}
                    className={`bg-white rounded-xl overflow-hidden cursor-pointer transition-all duration-300 border-3 ${
                      reservationData.bikeType === type.id
                        ? 'border-black shadow-xl scale-105'
                        : 'border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-lg'
                    }`}
                  >
                    <div className="h-32 bg-gray-50 flex items-center justify-center p-4">
                      <img 
                        src={type.icon} 
                        alt={type.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="p-5 text-center">
                      <h4 className="font-bold text-lg mb-2">{type.name}</h4>
                      <p className="text-sm text-gray-600">{type.description}</p>
                      {reservationData.bikeType === type.id && (
                        <div className="mt-3">
                          <svg className="w-8 h-8 mx-auto text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="flex justify-center">
            <div className="max-w-4xl">
              <h3 className="text-2xl font-bold text-center mb-6">Choisissez votre forfait</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {forfaits.map((forfait, index) => (
                  <div 
                    key={forfait.id}
                    onClick={() => handleForfaitSelect(forfait)}
                    className={`bg-white rounded-xl overflow-hidden cursor-pointer transition-all duration-300 border-3 ${
                      reservationData.forfait === forfait.nom 
                        ? 'border-black shadow-xl scale-105' 
                        : 'border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-lg'
                    } ${index === 0 ? 'ring-2 ring-amber-400' : ''}`}
                  >
                    <div className="h-32 overflow-hidden">
                      <img 
                        src={forfait.image}
                        alt={forfait.nom}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-bold text-lg">{forfait.nom}</h4>
                        {index === 0 && (
                          <span className="bg-amber-400 text-white text-xs px-2 py-0.5 rounded-full font-medium">TOP</span>
                        )}
                      </div>
                      <p className="text-gray-500 text-sm mb-3">{forfait.sousTitre}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-3xl font-bold">{forfait.prix}€</p>
                        {reservationData.forfait === forfait.nom && (
                          <svg className="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="flex justify-center">
            <div className="bg-white rounded-xl p-8 max-w-lg w-full shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold mb-6 text-center">Vos coordonnées</h3>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom complet <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={reservationData.name}
                    onChange={handleInputChange}
                    placeholder="Jean Dupont"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={reservationData.email}
                    onChange={handleInputChange}
                    placeholder="jean.dupont@exemple.fr"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Téléphone <span className="text-red-500">*</span>
                  </label>
                  <div className="flex">
                    <div className="flex items-center px-4 bg-gray-50 border-2 border-r-0 border-gray-200 rounded-l-lg">
                      <span className="text-lg">🇫🇷</span>
                      <span className="ml-2 text-sm text-gray-700 font-medium">+33</span>
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={reservationData.phone}
                      onChange={handleInputChange}
                      placeholder="6 12 34 56 78"
                      className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-r-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 5:
        const bikeTypeName = bikeTypes.find(t => t.id === reservationData.bikeType)?.name || '';
        
        return (
          <div className="flex justify-center">
            <div className="bg-white rounded-xl p-8 max-w-lg w-full shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-center mb-6">Récapitulatif de votre réservation</h3>
              
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Date & Heure</p>
                  <p className="font-semibold capitalize">{formatSelectedDate(reservationData.date)}</p>
                  <p className="text-sm text-gray-600 mt-1">à {reservationData.timeSlot}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Type de vélo</p>
                  <p className="font-semibold">{bikeTypeName}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Forfait sélectionné</p>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold">{reservationData.forfait}</p>
                      <p className="text-sm text-gray-500">{reservationData.forfaitDescription}</p>
                    </div>
                    <p className="text-2xl font-bold">{reservationData.prix}€</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 mb-2">Vos informations</p>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-semibold">Nom :</span> {reservationData.name}</p>
                    <p><span className="font-semibold">Email :</span> {reservationData.email}</p>
                    <p><span className="font-semibold">Téléphone :</span> +33 {reservationData.phone}</p>
                  </div>
                </div>

                <div className="border-t-2 border-gray-200 pt-4 mt-6">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold">Total à payer</span>
                    <span className="text-3xl font-bold">{reservationData.prix}€</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 min-h-[80vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold text-center text-black mb-4">
            Réserver mon lavage vélo
          </h1>
          <p className="text-center text-gray-600 mb-12">
            Un service professionnel en quelques clics
          </p>

          {/* Progress Steps */}
          <div className="max-w-3xl mx-auto mb-12">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center flex-1">
                    <div 
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                        currentStep === step.number
                          ? 'bg-black text-white scale-110'
                          : currentStep > step.number
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {currentStep > step.number ? (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        step.number
                      )}
                    </div>
                    <p className={`text-xs mt-2 text-center font-medium ${
                      currentStep === step.number ? 'text-black' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1 transition-all ${
                      currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Content */}
          <div 
            className="relative rounded-2xl overflow-hidden min-h-[500px] mb-8"
            style={{
              backgroundImage: `url('/lavage/d1f369e76c5b9c2ead7a8a7d24eb1607.jpg')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute inset-0 bg-white/85"></div>
            
            <div className="relative z-10 p-8 md:p-12">
              <div className="min-h-[400px] flex items-center justify-center">
                {renderStepContent()}
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center mt-8 max-w-2xl mx-auto">
                {currentStep > 1 ? (
                  <button
                    onClick={() => setCurrentStep(currentStep - 1)}
                    className="flex items-center gap-2 px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Retour
                  </button>
                ) : <div></div>}
                
                {currentStep < 5 ? (
                  <button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold transition-all ${
                      canProceed()
                        ? 'bg-black text-white hover:bg-gray-800 shadow-md hover:shadow-lg'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Continuer
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={handlePayment}
                    className="flex items-center gap-2 bg-green-600 text-white px-10 py-4 rounded-lg font-bold hover:bg-green-700 transition-colors shadow-lg"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Procéder au paiement
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  );
};

export default ReservationLavage;
